import {
  EyeInvisibleOutlined,
  EyeOutlined,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Input, Row, Spin } from "antd";
import { useState } from "react";
import { SuccessMessage } from "../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../../atoms/Toasts/ErrorMessage";
import { changePassword } from "../../ducks/services";
import { SubmitHandler, useForm } from "react-hook-form";

const antIcon = <Loading3QuartersOutlined spin />;

interface PropsType {
  onClose: () => void;
  id: string;
}

interface FormValues {
  password: string;
  confirmPassword: string;
}

export default function ChangePasswordPopUp(props: PropsType) {
  const { onClose, id } = props;
  const { handleSubmit } = useForm<FormValues>({
    mode: "onChange",
  });
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  const onSubmit: SubmitHandler<FormValues> = () => {
    setLoading(true);

    if (id) {
      const payload = {
        userId: id,
        newPassword: newPassword,
      };

      changePassword(payload)
        .then(() => {
          setLoading(false);
          SuccessMessage("New password saved.");
          onClose();
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const isPasswordValid = () => {
    return (
      newPassword.length >= 8 &&
      /[A-Z]/.test(newPassword) &&
      /[a-z]/.test(newPassword) &&
      /[0-9]/.test(newPassword) &&
      /[_!@#$%^&\-*]/.test(newPassword)
    );
  };

  console.log(
    "!isPasswordValid() || newPassword === confirmNewPassword",
    !isPasswordValid(),
    newPassword,
    confirmNewPassword,
    newPassword === confirmNewPassword
  );

  return (
    <Spin indicator={antIcon} size="large" spinning={loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        autoComplete="off"
        name="dependencies"
      >
        <div className="modalWrapper">
          <div className="modal_header">
            <div className="title">
              <h2>Change password</h2>
              <p>
                The new password will be applied immediately, with no need to
                re-login.
              </p>
            </div>
          </div>

          <div className="modal_content">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={
                    <>
                      <b>New password</b>{" "}
                      <span style={{ color: "#0B5167", marginLeft: 4 }}>*</span>
                    </>
                  }
                  name="password"
                  rules={[
                    { required: true, message: "New password is required." },
                  ]}
                >
                  <Input.Password
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setShowPasswordRequirements(true);
                    }}
                    onBlur={() => setShowPasswordRequirements(false)}
                    placeholder="Enter new password"
                    iconRender={(visible: boolean) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    className="formControl"
                  />
                </Form.Item>
                {showPasswordRequirements && !isPasswordValid() && (
                  <div className="passwordRequirements">
                    <h4>
                      {newPassword &&
                      newPassword.length >= 8 &&
                      /[A-Z]/.test(newPassword) &&
                      /[a-z]/.test(newPassword) &&
                      /[_!@#$%^&\-*]/.test(newPassword)
                        ? "Password is good to go"
                        : "Set a password"}
                    </h4>
                    <p>Your password must include:</p>
                    <ul>
                      <li>
                        <Checkbox disabled checked={newPassword.length >= 8}>
                          8+ characters
                        </Checkbox>
                      </li>
                      <li>
                        <Checkbox
                          disabled
                          checked={
                            /[A-Z]/.test(newPassword) &&
                            /[a-z]/.test(newPassword)
                          }
                        >
                          Upper & lower case letters
                        </Checkbox>
                      </li>
                      <li>
                        <Checkbox
                          disabled
                          checked={/[_!@#$%^&\-*]/.test(newPassword)}
                        >
                          Symbols (!@#$%^&*)
                        </Checkbox>
                      </li>
                    </ul>
                  </div>
                )}
              </Col>
              <Col span={24} className="mb-0">
                <Form.Item
                  label={
                    <>
                      <b>Confirm new password</b>
                      <span style={{ color: "#0B5167", marginLeft: 4 }}>*</span>
                    </>
                  }
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Confirm new password is required.",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords don't match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    iconRender={(visible: boolean) =>
                      visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                    }
                    className="formControl"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="modal_footer" style={{ justifyContent: "flex-end" }}>
            <Button
              htmlType="button"
              type="default"
              size="large"
              className="outlined-btn"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              className="mint-btn"
              disabled={
                !isPasswordValid() || newPassword !== confirmNewPassword
              }
            >
              Save password
            </Button>
          </div>
        </div>
      </Form>
    </Spin>
  );
}
